import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';
import fetch from 'isomorphic-unfetch';
import { AppContext } from '@prisjakt/ui-app-context';

class AbortError extends Error {
  constructor(message) {
    super(message);
    this.code = 'elasticsearch.abort';
  }
}

export default (buildQuery, throttleTime) => Component => {
  class ElasticContainer extends React.PureComponent {
    constructor(props) {
      super(props);

      this.query = null;
      this.mounted = false;

      this.state = {
        error: null,
        data: null,
        fetching: true,
      };

      this.queryId = 0;

      if (throttleTime) {
        this.fetch = throttle(this.fetch.bind(this), throttleTime);
      }
    }

    buildQuery() {
      return buildQuery(this.props.currentState, this.props);
    }

    execQuery() {
      const query = this.buildQuery();
      const queryId = this.queryId + 1;

      this.queryId = queryId;
      this.queryHash = JSON.stringify(query);

      clearTimeout(this.fetchTimer);
      this.fetchTimer = setTimeout(
        () => {
          this.setState({ fetching: true });
        },
        this.lastQuery && query.from !== this.lastQuery.from ? 0 : 1000
      );

      this.lastQuery = query;

      return fetch(
        [
          this.context.market.campaignsBaseUrl,
          `campaign-${this.context.market.code}-${this.props.campaignId}-deals/_search`,
        ].join('/'),
        {
          body: this.queryHash,
          method: 'POST',
        }
      ).then(res => {
        if (queryId !== this.queryId) {
          throw new AbortError('Results arrived to late, abort');
        }

        return res.json();
      });
    }

    getField(key, state) {
      if (this.props[key] && typeof this.props[key] !== 'function') {
        return this.props[key];
      }

      if (typeof this.props[key] === 'function') {
        return this.props[key](state);
      }

      return null;
    }

    shouldFetch() {
      return (
        this.props.fetch && this.queryHash !== JSON.stringify(this.buildQuery())
      );
    }

    fetch() {
      this.execQuery()
        .then(data => {
          clearTimeout(this.fetchTimer);

          if (this.mounted) {
            requestAnimationFrame(() => {
              this.setState({ error: null, data, fetching: false });
            });

            if (this.props.onSuccess) {
              this.props.onSuccess(data);
            }
          }
        })
        .catch(error => {
          if (error && error.code === 'elasticsearch.abort') {
            return;
          }

          clearTimeout(this.fetchTimer);

          if (this.mounted) {
            requestAnimationFrame(() => {
              this.setState({ error, data: null, fetching: false });
            });

            if (this.props.onError) {
              this.props.onError(error);
            }
          }
        });
    }

    componentDidMount() {
      this.mounted = true;
      if (this.shouldFetch()) {
        this.fetch();
      }
    }

    componentWillUnmount() {
      this.mounted = false;
    }

    componentDidUpdate() {
      if (this.shouldFetch()) {
        this.fetch();
      }
    }

    render() {
      return (
        <Component
          {...this.props}
          fetching={this.state.fetching}
          error={this.state.error || this.props.initialError}
          data={this.state.data || this.props.initialData}
        />
      );
    }
  }

  const { func, object, number, bool, node } = PropTypes;

  ElasticContainer.propTypes = {
    shouldFetch: func,
    campaignId: number.isRequired,
    onSuccess: func,
    onError: func,
    children: node,
    dispatch: func.isRequired,
    initialData: object, // eslint-disable-line react/forbid-prop-types
    initialError: object, // eslint-disable-line react/forbid-prop-types
    fetch: bool,
    currentState: object.isRequired, // eslint-disable-line react/forbid-prop-types
  };

  ElasticContainer.defaultProps = {
    shouldFetch: () => true,
    onSuccess: () => {},
    onError: () => {},
    children: null,
    fetch: true,
    initialData: undefined,
    initialError: undefined,
  };

  ElasticContainer.contextType = AppContext;

  return connect(state => ({
    currentState: state.areas.campaign,
  }))(ElasticContainer);
};
